<template>
  <div class="filter-category">
    <div class="filters">
      <div class="filters-sidebar-wrapper mobile-only">
        <!-- Mobile Filters -->
        <FiltersSidebar
          v-if="cleanedFacets && cleanedFacets.length > 0"
          :open-filters="openFilters"
          :products-categories-names="productsCategoriesNames"
          :sort-by="sortBy"
          :loading="loading"
          :disableResetFilter="disableResetFilter"
          :openAccordions="openAccordions"
          :cleanedFacets="cleanedFacets"
          :facetsMap="facetsMap"
          :activeFilters="activeFilters"
          @closeSidebar="openFilters = false"
          @close="toggleFilters"
          @filtersCount="(e) => (filtersCount = e)"
          @removeFilter="
            (e) => removeFilter(e.activeFacetOption, e.activeFacet)
          "
          @resetFilters="uiHelpers.changeFilters({})"
          @accordionClick="(e) => handleClick(e.name, e.target)"
          @filterClick="(e) => handleClick(e.name, e.target, e.option)"
          @applyFilters="applyFilters"
          @sortByClick="
            (e) => {
              activeSorting = e.label;
              th.changeSorting(e.value);
            }
          "
        />
        <div class="filters-sidebar-cta-wrapper" @click="toggleFilters">
          <span class="filters-sidebar-cta">
            {{ $t('Filters / Order by') }}
            <!--          <span v-if="filtersCount > 0">({{ filtersCount }})</span>-->
          </span>
        </div>
      </div>
      <!-- Desktop Filters -->
      <div class="category-filters-wrapper desktop-only">
        <DesktopFilters
          v-if="cleanedFacets && cleanedFacets.length > 0"
          :products-categories-names="productsCategoriesNames"
          :sort-by="sortBy"
          :loading="loading"
          :disableResetFilter="disableResetFilter"
          :openAccordions="openAccordions"
          :cleanedFacets="cleanedFacets"
          :facetsMap="facetsMap"
          :activeFilters="activeFilters"
          @filtersCount="(e) => (filtersCount = e)"
          @removeFilter="
            (e) => removeFilter(e.activeFacetOption, e.activeFacet)
          "
          @resetFilters="uiHelpers.changeFilters({})"
          @accordionClick="(e) => handleClick(e.name, e.target)"
          @filterClick="(e) => handleClick(e.name, e.target, e.option)"
          @applyFilters="applyFilters"
          @sortByClick="
            (e) => {
              activeSorting = e.label;
              th.changeSorting(e.value);
            }
          "
        />
      </div>
    </div>
  </div>
</template>

<script>
import { SfSidebar } from '@storefront-ui/vue';
import FiltersSidebar from '~/components/Sidebars/Filters/FiltersSidebar.vue';
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  toRefs,
  watch,
} from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { useI18n } from '~/helpers/hooks/usei18n';
import { useUiHelpers } from '~/composables';
import { facetGetters } from '@gemini-vsf/composables';
import { IconCloseBlack } from '~/components/General/Icons';
import CustomSidebar from '~/components/General/CustomSidebar.vue';
import DesktopFilters from '~/components/Sidebars/Filters/DesktopFilters.vue';

export default defineComponent({
  name: 'CategoryFilter',
  components: {
    DesktopFilters,
    CustomSidebar,
    FiltersSidebar,
    SfSidebar,
    IconCloseBlack,
  },
  props: {
    categoryId: {
      type: String,
      required: true,
    },
    searchSpecsStr: {
      type: String,
      required: true,
    },
    productsCategoriesNames: {
      type: Object,
      default: () => {},
    },
    result: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props) {
    const {
      $gemini: { config: geminiConfig },
    } = useVSFContext();
    const uiHelpers = useUiHelpers();
    const trans = useI18n();
    const th = useUiHelpers();
    const openFilters = ref(false);
    const filtersCount = ref(0);
    const openSorting = ref(false);
    const activeSorting = ref('');
    const activeFilters = ref([]);
    const openAccordions = ref([]);

    const facetsMap = new Map([
      ['cats_ids', trans.t('Product')],
      ['sdc_color_mount_terms', trans.t('Mount')],
      ['sdc_colore_vetro_terms', trans.t('Glass Color')],
      ['sdc_numero_luci_terms', trans.t('Lights')],
      ['sdc_diametro_terms', trans.t('Diameter')],
      ['sdc_larghezza_terms', trans.t('Length')],
      ['sdc_altezza_terms', trans.t('Height')],
      ['sdc_profondita_terms', trans.t('Depth')],
    ]);

    watch(openFilters, () => {
      if (!openFilters) {
        openAccordions.value = [];
      }
    });

    const { filters } = uiHelpers.getFacetsFromURL();

    const getSelectedFilterValues = () => {
      const selectedFilterValues = Object.fromEntries(
        geminiConfig.facets.available.map((current) => [current, []])
      );
      Object.keys(filters).forEach((filter) => {
        selectedFilterValues[filter] = filters[filter];
      });
      return selectedFilterValues;
    };

    const filterState = reactive({
      sortBy: [],
      facets: [],
      filtersJson: getSelectedFilterValues(),
    });

    const disableResetFilter = computed(() => {
      const selectedFilters = Object.values(filterState.filtersJson);
      return selectedFilters.every((e) => e.length === 0);
    });

    // eslint-disable-next-line no-return-assign
    const removeEmptyFilters = () =>
      (filterState.filtersJson = Object.fromEntries(
        Object.entries(filterState.filtersJson).filter(
          ([_, value]) => value.length > 0
        )
      ));

    const getCategoryLabels = () => {
      // Populate cats_ids options label using the object created in Category.vue
      filterState.facets?.forEach((facet) => {
        if (facet.id === 'cats_ids' || facet.label === 'cats_ids') {
          const facetOptions = [];
          const unwantedCategoryLabels = new Set(['Products', 'Prodotti']);
          // Clone array to remove values from original without messing indexes
          facet.options.forEach((opt) => facetOptions.push(opt));
          facetOptions.forEach((option) => {
            // Remove options with empty or "Products" label
            if (
              props.productsCategoriesNames?.[option.value?.split('::')[1]] &&
              !!props.productsCategoriesNames?.[option.value?.split('::')[1]] &&
              !unwantedCategoryLabels.has(
                props.productsCategoriesNames?.[option.value.split('::')[1]]
              )
            ) {
              option.attrName =
                props.productsCategoriesNames?.[option.value?.split('::')[1]];
            } else {
              facet.options.splice(facet.options.indexOf(option), 1);
            }
          });
          facet.options.filter(Boolean);
        }
      });
    };

    // Order category options by label
    const sortCategories = () => {
      filterState.facets?.[0]?.options.sort((a, b) =>
        a.attrName > b.attrName ? 1 : a.attrName < b.attrName ? -1 : 0
      );
    };

    const selectedFilters = ref(getSelectedFilterValues());

    const populateFacets = () => {
      if (!props.result && !props.result.data) {
        return;
      }
      filterState.sortBy = facetGetters.getSortOptions(props.result);
      filterState.facets = facetGetters
        .getGrouped(props.result, geminiConfig.facets.available)
        // Put category filter in first position
        ?.sort((a) => (a.label === 'cats_ids' ? -1 : 1));
      getCategoryLabels();
      sortCategories();
      filterState.filtersJson = getSelectedFilterValues();

      if (filterState.facets && filterState.facets.length > 0) {
        filterState.facets.forEach((facet) => {
          facet.options.forEach((option) => {
            if (
              option.selected === true &&
              !activeFilters.value.includes(option.attrName)
            ) {
              activeFilters.value.push(option.attrName);
            }
          });
        });
      }
    };

    populateFacets();

    onMounted(() => populateFacets());

    watch(props.result, () => {
      populateFacets();
    });

    const removeFilter = (option, facet) => {
      filterState.filtersJson[facet.id] = filterState.filtersJson[
        facet.id
      ].filter((e) => e !== option.value);
      filterState.facets
        .find((e) => e.id === facet.id)
        .options.find((index) => index.value === option.value).selected = false;
      removeEmptyFilters();
    };

    const handleClick = (e, target, option = null) => {
      let localReference =
        target === 'filter' ? activeFilters.value : openAccordions.value;
      if (localReference.includes(e)) {
        const indexToRemove = localReference.indexOf(e);
        localReference.splice(indexToRemove, 1);
      } else {
        if (target === 'desktopAccordion') {
          localReference = [];
        }
        localReference.push(e);
      }
      if (target === 'filter') {
        activeFilters.value = localReference;
      } else {
        openAccordions.value = localReference;
      }

      if (option) {
        if (filterState.filtersJson[option.facet]) {
          if (
            filterState.filtersJson[option.facet].includes(option.option.value)
          ) {
            removeFilter(
              option.option,
              filterState.facets.find((v) => v.id === option.facet)
            );
            // filterState.filtersJson[option.facet] = filterState.filtersJson[
            //   option.facet
            // ].filter((o) => o !== option.option.value);
          } else {
            filterState.filtersJson[option.facet].push(option.option.value);
          }
        } else {
          filterState.filtersJson[option.facet] = [];
          filterState.filtersJson[option.facet].push(option.option.value);
        }

        removeEmptyFilters();
      }
    };

    const applyFilters = () => {
      uiHelpers.changeFilters(filterState.filtersJson);
    };

    const cleanedFacets = computed(() =>
      filterState?.facets?.length > 0
        ? filterState.facets.filter(
            (facet) =>
              // Filter out dimensions, query not supporting range yet
              // ToDo: use facets to create dimensions and price filters once range is supported
              facet.label !== 'sdc_diametro_terms' &&
              facet.label !== 'sdc_larghezza_terms' &&
              facet.label !== 'sdc_altezza_terms' &&
              facet.label !== 'sdc_profondita_terms' &&
              facet.options.length > 1
          )
        : filterState.facets
    );

    let selectedFiltersCopy = JSON.parse(JSON.stringify(selectedFilters.value));

    const isFilterSelected = (facet, option) => {
      if (facet.id === 'price') {
        return selectedFiltersCopy[facet.id];
      }

      return (selectedFiltersCopy[facet.id] || []).includes(option.value);
    };

    const selectFilter = (facet, option) => {
      if (facet.id === 'price') {
        selectedFiltersCopy[facet.id] = option.value;
        return;
      }

      if (!selectedFiltersCopy[facet.id]) {
        selectedFiltersCopy[facet.id] = [];
      }

      // eslint-disable-next-line unicorn/prefer-array-some
      if (selectedFiltersCopy[facet.id].find((f) => f === option.value)) {
        selectedFiltersCopy[facet.id] = selectedFiltersCopy[facet.id]?.filter(
          (f) => f !== option.value
        );
        return;
      }
      selectedFiltersCopy[facet.id].push(option.value);
    };

    const { changeFilters } = useUiHelpers();

    const resetFilter = (facet) => {
      selectedFiltersCopy[facet.id] = [];
      changeFilters(selectedFiltersCopy);
    };

    const selectedFiltersCount = (facet) => {
      if (selectedFiltersCopy) {
        return selectedFiltersCopy[facet.id]?.length;
      }
      return 0;
    };

    function toggleFilters() {
      openFilters.value = !openFilters.value;
    }

    return {
      trans,
      th,
      isFilterSelected,
      selectFilter,
      applyFilters,
      selectedFiltersCount,
      resetFilter,
      selectedFiltersCopy,
      geminiConfig,
      openFilters,
      toggleFilters,
      filtersCount,
      openSorting,
      activeSorting,
      openAccordions,
      handleClick,
      activeFilters,
      uiHelpers,
      facetsMap,
      removeFilter,
      ...toRefs(filterState),
      cleanedFacets,
      disableResetFilter,
    };
  },
});
</script>

<style lang="scss">
.filters {
  .filters-sidebar-wrapper {
    .filters-sidebar-cta-wrapper {
      width: 100%;
      height: 3.375rem;
      border-top: 1px solid var(--c-black);
      border-bottom: 1px solid var(--c-black);
      @include flex-center;
      @include pointer;
      .filters-sidebar-cta {
        @include desktop-h7;
      }
    }

    .desktop-only {
      @include for-mobile {
        display: none;
      }
    }

    .mobile-only {
      @include for-desktop {
        display: none;
      }
    }
  }

  .filter {
    margin-left: 25px;
    position: relative;
    z-index: 2;

    .navbar__sort {
      .navbar__select,
      p {
        display: inline-block;
      }
    }
    .navbar__sort:hover {
      cursor: pointer;
    }

    .sf-component-select__selected {
      position: relative;
      padding: 12px 15px;
      margin-bottom: 0;
      &:after {
        background-repeat: no-repeat;
        content: '';
        display: block;
        height: 16px;
        margin-left: 10px;
        width: 16px;
      }
    }
    .sf-component-select {
      padding-bottom: 0;
      .sf-component-select__error-message {
        display: none;
      }
    }
    .sf-component-select.is-active {
      background: #fff;
      .sf-component-select__selected:after {
        transform: rotate(180deg);
      }
    }

    .sf-component-select-option:first-child {
      border-top: 0;
    }

    .sf-component-select__dropdown {
      right: 0;
      left: auto;
      min-width: 288px;
      box-shadow: none;
    }
    .sf-component-select__dropdown {
      .sf-product-option__label {
        font-family: 'Spartan';
        font-size: 12px;
        font-weight: 500;
        line-height: 23px;
      }
    }

    .sf-product-option__label {
      color: #222a24;
      font-family: 'Spartan';
      font-size: 16px;
      font-weight: 500;
      line-height: 23px;
    }

    .filter-content .filter-relation-content {
      .relation-section {
        border-bottom: 1px solid #e9ecf1;
        padding: 15px 0;
        position: relative;

        span {
          max-width: 150px;
          word-wrap: break-word;
        }

        &:last-child {
          border: 0;
        }
      }
    }

    .filter-name {
      align-items: center;
      color: #222a24;
      display: flex;
      font-family: 'Spartan';
      font-size: 16px;
      font-weight: 500;
      line-height: 23px;
      padding: 12px 15px;
      pointer-events: none;
      text-align: right;
      &:after {
        background-repeat: no-repeat;
        content: '';
        display: block;
        height: 16px;
        margin-left: 10px;
        width: 16px;
        z-index: 222;
      }
      span {
        font-family: inherit;
        font-size: inherit;
      }
      .filter-count {
        font-size: 11px;
      }
    }
    &.active {
      .filter-name {
        background: #fff;
      }

      .filter-name:after {
        transform: rotate(180deg);
      }
    }

    &.active .filter-content {
      display: flex;
      opacity: 1;
    }
  }

  &.only-mobile {
    display: none;
  }
}

.filter.mobile {
  .navbar__sort.mobile {
    .sort-items {
      border-bottom: 1px solid var(--c-grey);
      padding: 0.625rem;
      @include pointer;

      &.first-items {
        border-top: 1px solid var(--c-grey);
      }
    }
  }
}

.mobile-filter-accordion {
  .sf-accordion-item__header {
    padding: 0 0 0 16px;
  }
  .sf-accordion-item__content {
    border: none;
  }
  .sf-button[aria-expanded='false'] {
    border: none;
  }
}

.filter {
  &.mobile {
    .filter-radio-group {
      .sf-radio {
        .checkmark {
          visibility: hidden;
        }
        &:not(:last-child) {
          border-bottom: 1px solid #eaecf2;
        }
        &.is-active {
          background-color: #ffffff;
          .checkmark {
            visibility: visible;
            display: flex;
            align-items: center;
          }
        }
        .sf-radio__container {
          padding: 8px 0;
          flex-direction: row-reverse;
          .sf-radio__content {
            margin: 0;
            .sf-radio__label {
              margin: 0.5em 0;
              color: grey;
              font-family: 'Frank Ruhl Libre', serif;
              font-weight: 400;
              white-space: normal;
            }
          }
        }
      }
    }
  }
}

.filter-content {
  background: #fff;
  display: none;
  flex-wrap: wrap;
  max-width: 420px;
  padding: 15px;
  position: absolute;
  right: 0;
  transition: opacity 0.7s;
  width: max-content;
  flex-direction: column;

  .filter-content-options {
    display: flex;
    flex-wrap: wrap;
  }

  &.relations {
    width: 100%;
  }

  .filter-relation {
    width: 100%;
  }

  &.mobile {
    display: flex;
    position: inherit;
    overflow-x: scroll;
    width: 100%;
    padding-left: 0;
    .filter-content-options {
      flex-wrap: nowrap;
      div {
        .box {
          align-items: center;
          border: 1px solid #222a24;
          display: flex;
          float: right;
          height: 34px;
          justify-content: center;
          width: 34px;
          position: relative;
          pointer-events: auto;
          overflow: hidden;
          &.is-active {
            .sf-checkbox__checkmark {
              display: none;
            }
          }
          .sf-checkbox__container {
            width: 100%;
            display: block;
            .sf-button {
              width: 100%;
              background-size: cover;
            }
            .sf-filter__label {
              top: 40%;
            }
          }
          &.is-color {
            .sf-filter__label {
              display: none;
            }
          }
          &.isSize {
            width: 40px;
            height: 40px;
          }
        }
      }
    }
  }

  .box {
    align-items: center;
    border: 1px solid #222a24;
    display: flex;
    float: right;
    height: 34px;
    justify-content: center;
    margin: 5px;
    width: 34px;
    position: relative;
    pointer-events: auto;
    overflow: hidden;
    .sf-checkbox__container {
      pointer-events: none;
    }
    .sf-filter__label {
      position: absolute;
      margin-left: 50%;
      transform: translateX(-50%);
      color: #222a24;
      font-family: 'Spartan';
      font-size: 12px;
      font-weight: 500;
      pointer-events: none;
    }
    .sf-checkbox__checkmark {
      border: none;
    }
    &:nth-child(9n + 1) {
      margin-left: 0;
    }
    &:nth-child(9n) {
      margin-right: 0;
    }
    &.active {
      background-color: #222a24;
      .color {
        background-color: #fff;
        border: 4px double #222a24;
      }
      .sf-filter__label {
        color: #fff;
      }
      span {
        color: #fff;
      }
    }

    &.active.color {
      background-color: #fff;
      border: 4px double #222a24;
      .sf-filter__label {
        color: #fff;
      }
      span {
        color: #fff;
      }
    }

    span {
      color: #222a24;
      font-family: 'Spartan';
      font-size: 12px;
      font-weight: 500;
      pointer-events: none;
    }

    &:hover {
      transform: scale(1);
    }
  }

  .filter-reset {
    font-size: 13px;
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
}

@media (max-width: 1023px) {
  .filter-color-content {
    display: block;
  }

  #category .color .sf-filter .sf-checkbox__container {
    position: relative;

    .sf-checkbox__checkmark {
      left: 0;
      position: absolute;
    }
  }
  .filter-content .box {
    height: 24px;
    width: 24px;
  }
  .filter-content .filter-size {
    .box {
      margin-top: 0;
    }
  }
  .filters {
    margin: 0;

    .filter {
      margin-left: 0;
      padding: 0;
      position: static;
      width: 100%;
      .sf-checkbox input:focus + .sf-checkbox__checkmark {
        outline: none;
      }

      .filter-name {
        padding: 15px 10px;
      }

      .filter-relation .filter-name {
        padding: 40px 10px 0;
      }

      .filter-notify {
        font-size: 11px;
        line-height: 20px;
        color: #5e6770;
        font-weight: 500;
        font-family: 'Spartan';
        padding: 10px;
        a {
          color: inherit;
          text-decoration: underline;
        }
      }

      .filter-title {
        align-items: center;
        color: #222a24;
        display: flex;
        pointer-events: none;
        position: relative;

        span {
          font-family: 'Spartan';
          font-weight: 500;
          text-transform: uppercase;
        }

        &:after {
          background-repeat: no-repeat;
          content: '';
          display: block;
          height: 16px;
          position: absolute;
          right: 0;
          width: 16px;
        }
      }
      .filter-content {
        flex-direction: column;
        max-width: 100%;
        width: 100%;

        .filter-content-options {
          display: flex;
          flex-wrap: wrap;
        }

        .filter-reset {
          font-size: 13px;
          text-decoration: underline;
          &:hover {
            cursor: pointer;
          }
        }

        .box {
          float: left;
          margin-right: 15px;
        }

        .filter-size-content .box {
          box-shadow: unset;
          border: 1px solid #222a24;
          height: 50px;
          width: 50px;
          pointer-events: auto;
          .sf-checkbox__checkmark {
            pointer-events: none;
          }
          .sf-checkbox__container {
            pointer-events: none;
          }

          .sf-filter__label {
            color: #222a24;
            font-size: 13px;
            pointer-events: none;
          }

          &.active {
            background: #2e3830;
            transform: scale(1);

            .sf-filter__label {
              color: #fff;
            }
          }
        }

        .filter-relation-content {
          padding: 0 10px;
          .sf-radio input:focus + .sf-radio__checkmark {
            outline-style: none;
            background-repeat: no-repeat;
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);
            height: 16px;
            width: 16px;
            height: 16px;
          }
          .relation-section {
            border-bottom: 1px solid #e9ecf1;
            padding: 15px 0;
            position: relative;
            .sf-radio__container {
              padding: 0;

              input {
                right: 15px;
                left: auto;
              }
              .sf-radio__checkmark {
                border: 0;
                left: auto;
                position: absolute;
                right: 15px;
              }
              .sf-radio__content {
                margin-left: 0;
                .sf-radio__label {
                  font-size: 11px;
                  font-weight: 500;
                  color: #5e6770;
                }
              }
            }

            &:last-child {
              border: 0;
            }
          }
        }

        .filter-action {
          margin: 30px 0 10px;
          padding: 0 10px;

          span {
            background: #212a24;
            color: #fff;
            display: block;
            font-family: 'Spartan';
            font-size: 10px;
            font-weight: 700;
            padding: 15px 0;
            text-align: center;
            text-transform: uppercase;
          }
        }
      }

      .filter-name span {
        font-size: 14px;
        font-weight: 500;
        color: #222a24;
      }

      .filter-name:after {
        background-image: unset;
        height: 0;
        width: 0;
      }
      &.active {
        .filter-title:after {
          transform: rotate(180deg);
        }
        .filter-content {
          display: block;
        }
      }
    }
    &.only-desktop {
      display: none;
    }
    &.only-mobile {
      display: flex;
    }
  }
}

body .size-list .size-item {
  max-width: 65px;
  width: 65px;
  &.glide__slide--clone {
    order: 1;
    opacity: 0;
    visibility: hidden;
  }
  &.glide__slide--active ~ .glide__slide--clone {
    order: 10;
    opacity: 1;
    visibility: unset;
  }
}

.filter-color-content {
  margin: 0 -3px;
}

.color {
  padding: 0 3px;
  max-width: 42px;
}

@media (max-width: 480px) {
  .filters .filter .filter-title span {
    font-size: 10px;
  }

  .filters
    .filter
    .filter-content
    .filter-relation-content
    .relation-section
    span {
    font-size: 11px;
    font-weight: 500;
    color: #5e6770;
  }
}
</style>

<style>
.box .sf-filter__color {
  height: 36px;
  width: 36px;
}

.box .sf-color:hover {
  transform: scale(1);
}

.color .sf-filter label.sf-checkbox__container {
  position: relative;
  pointer-events: none;
}

.color .sf-checkbox {
  padding: 5px;
  pointer-events: auto;
  border: 1px solid transparent;
}

.color .sf-checkbox.active {
  border: 1px solid #222a24;
}

.color .sf-filter label.sf-checkbox__container .sf-checkbox__checkmark {
  position: absolute;
  left: 0;
}

.color .sf-filter__color {
  margin: 0;
  width: 24px;
  height: 24px;
}

.color .sf-checkbox__message {
  margin: 0;
}

.color .sf-filter__label,
.color .sf-filter__count {
  position: absolute;
}

@media (min-width: 1024px) {
  .mobile-only {
    display: none;
  }
}

@media (max-width: 1023px) {
  .desktop-only {
    display: none;
  }
}
</style>
